// Implements: F-PRV-007, F-PRV-008, F-PRV-009, F-PRV-002, F-PRV-005, F-PRV-006, F-PRV-011, F-PRV-012, F-PRV-013, F-PRV-016
/**
 * The viewer of one mockup's current version.
 *
 * It owns what the viewer remembers: which version is SHOWN, the viewport, the reload count and
 * full screen. The shown version only moves when a generation is not running (design 5.1): while a
 * run is open the frame is neither re-created nor re-navigated, and a run that ends badly leaves the
 * last complete version on screen with a neutral message and a way to try again, never a partial
 * document. The caller keys this container by mockup so that no state leaks between mockups; the
 * shown version is also re-checked against the mockup id it was adopted for.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { previewDocumentPath, previewDownloadPath } from "../../../shared/ui/frameDocumentPath";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { SandboxedFrame } from "../../../shared/ui/atoms/SandboxedFrame";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { tabPanelProps } from "../../../shared/ui/molecules/Tabs";
import { ConsolePanel } from "../components/ConsolePanel";
import { PreviewToolbar } from "../components/PreviewToolbar";
import type { PreviewTab } from "../components/PreviewToolbar";
import { PreviewViewport } from "../components/PreviewViewport";
import { SourceTabs } from "../components/SourceTabs";
import type { SourcePieces } from "../components/SourceTabs";
import { useFrameRelay } from "../hooks/useFrameRelay";
import type { RelayedMessage } from "../hooks/useFrameRelay";
import { previewDownloadAvailable } from "../model/previewFeatures";
import { clampZoom, frameWidth } from "../model/viewport";
import type { ViewportMode, ViewportState } from "../model/viewport";
import { readViewport, writeViewport } from "../model/viewportMemory";
import styles from "./PreviewContainer.module.css";

export interface PreviewSource {
  readonly versionId: string;
  readonly pieces: SourcePieces;
}

export type GenerationState = "idle" | "running" | "failed" | "cancelled" | "lost";

export interface PreviewContainerProps {
  readonly mockupId: string;
  /** The mockup's last COMPLETE version; `null` when it has none. */
  readonly versionId: string | null;
  readonly generation?: GenerationState | undefined;
  readonly onRetry?: (() => void) | undefined;
  /** Messages the frame relayed, already validated and capped by this side (W3f.7). */
  readonly onRelay?: ((message: RelayedMessage) => void) | undefined;
  /**
   * The code the caller has read, with the version it belongs to; it is displayed as text, and only
   * while that version is the one in the frame.
   */
  readonly source?: PreviewSource | undefined;
  /**
   * How the caller's read of the source stands: `loading` shows a waiting line, `error` a neutral
   * failure with `onSourceRetry`. Absent when the caller reads no source.
   */
  readonly sourceStatus?: "loading" | "error" | "ready" | undefined;
  /** Reads the source again; offered only after a failed read (no automatic retry). */
  readonly onSourceRetry?: (() => void) | undefined;
  /** The share control, drawn right after the tabs (UX-06). */
  readonly share?: ReactNode;
  /** Drawn over the canvas, at its top (the banner of a previewed version). */
  readonly banner?: ReactNode;
}

interface Shown {
  readonly mockupId: string;
  readonly versionId: string | null;
}

const IGNORE = (): void => undefined;
const NO_MESSAGES: readonly RelayedMessage[] = [];

type InterruptionKey =
  | "preview.interrupted.failed"
  | "preview.interrupted.emptyFailed"
  | "preview.interrupted.cancelled"
  | "preview.interrupted.lost";

function interruptionKey(generation: GenerationState, hasVersion: boolean): InterruptionKey | null {
  if (generation === "failed") {
    return hasVersion ? "preview.interrupted.failed" : "preview.interrupted.emptyFailed";
  }
  if (generation === "cancelled") return "preview.interrupted.cancelled";
  if (generation === "lost") return "preview.interrupted.lost";
  return null;
}

export function PreviewContainer({
  mockupId,
  versionId,
  generation = "idle",
  onRetry,
  onRelay,
  source,
  sourceStatus,
  onSourceRetry,
  share,
  banner,
}: PreviewContainerProps): ReactElement {
  const { t } = useTranslation();
  const tabsId = `preview-${useId()}`;
  // The content tab: the frame stays mounted behind the code and the console, so switching never
  // reloads the document nor empties its console.
  const [tab, setTab] = useState<PreviewTab>("preview");

  // The version on screen. A new one is adopted only when the run is over and finished well
  // (`idle`), or when nothing is shown yet and no run is open; another mockup starts again.
  const [shown, setShown] = useState<Shown>(() => ({
    mockupId,
    versionId: generation === "running" ? null : versionId,
  }));
  const sameMockup = shown.mockupId === mockupId;
  const adopt =
    generation === "idle" || (generation !== "running" && (shown.versionId === null || !sameMockup));
  if ((!sameMockup && generation !== "running") || (adopt && shown.versionId !== versionId)) {
    setShown({ mockupId, versionId: adopt ? versionId : null });
  }
  const current = sameMockup ? shown.versionId : null;

  const [viewport, setViewport] = useState<ViewportState>(readViewport);
  const [available, setAvailable] = useState<number | null>(null);
  const [reloads, setReloads] = useState(0);
  const [native, setNative] = useState(false);
  const [maximised, setMaximised] = useState(false);

  const viewerRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const fullscreenRef = useRef<HTMLButtonElement | null>(null);

  const frameKey = `${mockupId}:${current ?? ""}:${String(reloads)}`;
  // The pane belongs to one load of the frame: a message is kept with the key it arrived under,
  // so a reload or another version starts with an empty console.
  const [log, setLog] = useState<{ readonly key: string; readonly messages: readonly RelayedMessage[] }>(
    { key: frameKey, messages: [] },
  );
  const onFrameMessage = useCallback(
    (message: RelayedMessage): void => {
      setLog((previous) => ({
        key: frameKey,
        messages: [...(previous.key === frameKey ? previous.messages : []), message],
      }));
      (onRelay ?? IGNORE)(message);
    },
    [frameKey, onRelay],
  );
  useFrameRelay(frameRef, frameKey, onFrameMessage);
  const messages = log.key === frameKey ? log.messages : NO_MESSAGES;

  // The space the stage offers, which bounds a free width. Without the API the width is unbounded.
  // The stage exists only while a version is shown, so that is what the observer depends on.
  const hasStage = current !== null;
  useEffect(() => {
    const stage = stageRef.current;
    if (!hasStage || stage === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) setAvailable(Math.floor(entry.contentRect.width));
    });
    observer.observe(stage);
    return () => {
      observer.disconnect();
    };
  }, [hasStage]);

  const changeViewport = useCallback((next: ViewportState): void => {
    setViewport(next);
    writeViewport(next);
  }, []);

  // Full screen is the wrapper's: the API reports back through `fullscreenchange`, and without it
  // the wrapper is maximised in the page.
  useEffect(() => {
    const onChange = (): void => {
      setNative(document.fullscreenElement === viewerRef.current);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
    };
  }, []);

  const full = native || maximised;
  const wasFull = useRef(false);
  useEffect(() => {
    if (wasFull.current && !full) fullscreenRef.current?.focus();
    wasFull.current = full;
  }, [full]);

  useEffect(() => {
    if (!maximised) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      // Claimed: this Escape leaves the maximised preview and nothing else (F-UIX-017).
      event.preventDefault();
      setMaximised(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [maximised]);

  const toggleFullscreen = (): void => {
    if (full) {
      if (native) {
        document.exitFullscreen().catch(() => {
          setNative(false);
        });
      }
      setMaximised(false);
      return;
    }
    const viewer = viewerRef.current;
    if (viewer !== null && typeof viewer.requestFullscreen === "function") {
      viewer.requestFullscreen().catch(() => {
        setMaximised(true);
      });
      return;
    }
    setMaximised(true);
  };

  const interruption = interruptionKey(generation, current !== null);

  const panel = (id: PreviewTab): ReturnType<typeof tabPanelProps> & { hidden: boolean } => ({
    ...tabPanelProps(tabsId, id),
    hidden: tab !== id,
  });
  // New tab: the INLINE frame route (it sends its own CSP with `sandbox`, so it stays inert at top
  // level). The attachment route would download the file instead of opening it.
  const openHref = current === null ? undefined : previewDocumentPath(mockupId, current);
  const downloadHref = current === null ? undefined : previewDownloadPath(mockupId, current);

  return (
    <section
      ref={viewerRef}
      className={styles.viewer}
      data-testid="preview-viewer"
      data-maximised={maximised ? "true" : undefined}
    >
      <PreviewToolbar
        idBase={tabsId}
        tab={tab}
        onTab={setTab}
        share={share}
        viewport={viewport}
        canReload={current !== null}
        openHref={openHref}
        downloadHref={previewDownloadAvailable() ? downloadHref : undefined}
        consoleCount={messages.length}
        fullscreen={full}
        fullscreenRef={fullscreenRef}
        onMode={(mode: ViewportMode) => {
          changeViewport({ ...viewport, mode });
        }}
        onFreeWidth={(width) => {
          changeViewport({ ...viewport, freeWidth: width });
        }}
        onZoom={(zoom) => {
          changeViewport({ ...viewport, zoom: clampZoom(zoom) });
        }}
        onReload={() => {
          setReloads((count) => count + 1);
        }}
        onToggleFullscreen={toggleFullscreen}
      />
      <div className={styles.panel} {...panel("preview")}>
        {banner}
        {interruption === null ? null : (
          <div className={styles.notice} role="status">
            <p className={styles.noticeText}>{t(interruption)}</p>
            {onRetry === undefined ? null : (
              <Button variant="secondary" size="sm" onClick={onRetry}>
                {t("common.action.retry")}
              </Button>
            )}
          </div>
        )}
        {current === null ? (
          <div className={styles.empty}>
            <EmptyState icon="monitor" title={t("preview.empty")} body={t("preview.emptyBody")} />
          </div>
        ) : (
          <PreviewViewport
            width={frameWidth(viewport, available)}
            zoom={clampZoom(viewport.zoom)}
            stageRef={stageRef}
            label={t("preview.stage.label")}
          >
            <SandboxedFrame
              key={frameKey}
              src={previewDocumentPath(mockupId, current)}
              title={t("preview.frame.title")}
              className={styles.frame}
              frameRef={frameRef}
            />
          </PreviewViewport>
        )}
      </div>
      <div className={styles.panel} {...panel("code")}>
        {current === null ? null : source?.versionId === current ? (
          <SourceTabs key={`${mockupId}:${current}`} pieces={source.pieces} />
        ) : sourceStatus === "error" ? (
          <div className={styles.notice} role="status">
            <p className={styles.noticeText}>{t("preview.source.failed")}</p>
            {onSourceRetry === undefined ? null : (
              <Button variant="secondary" size="sm" onClick={onSourceRetry}>
                {t("preview.source.retry")}
              </Button>
            )}
          </div>
        ) : source !== undefined || sourceStatus === "loading" ? (
          <p className={styles.hint}>{t("preview.source.loading")}</p>
        ) : null}
      </div>
      <div className={styles.panel} {...panel("console")}>
        {current === null ? null : <ConsolePanel messages={messages} />}
      </div>
    </section>
  );
}
